/**
 * audio-peaks-stream.js — огибающая (пики) для файла ЛЮБОЙ длины и размера,
 * без загрузки его целиком куда бы то ни было.
 *
 * ЗАЧЕМ ЭТО ЕСТЬ (живой случай, замерено, а не предположено)
 * ─────────────────────────────────────────────────────────
 * Екатерина, психолог: консультация 3 ч 05 мин, m4a 207 МБ, Android с 4 ГБ.
 * `/trim` не открывал файл ВООБЩЕ — вкладку убивало на «Декодирование 95 %».
 * Три поколения починок, каждое лучше предыдущего и каждое недостаточное:
 *
 *   1) полный decodeAudioData ............ 4058 МБ PCM — безнадёжно;
 *   2) понижение частоты декодера ........ пол 3000 Гц → всё равно сотни МБ,
 *      и понижение уважает только ПЕРВЫЙ ярус AudioDecoderRobust: на запасном
 *      буфер приходит на исходной частоте (замерено: 1817 МБ);
 *   3) FFmpeg → прореженный PCM .......... 44 МБ результата, НО FFmpeg обязан
 *      проглотить весь файл: 207 МБ в его памяти поверх 198 МБ, уже лежащих в
 *      кэше байтов. На её телефоне это не доходит до конца — телеметрия
 *      08-11: `wasm_ready` есть, `peaks_lane` НЕТ, `tool_abandon`.
 *
 * Здесь — четвёртое поколение и единственное, где расход НЕ ЗАВИСИТ ни от
 * длительности, ни от размера: файл читается СРЕЗАМИ прямо с диска, кадры
 * декодируются по одному через WebCodecs, наружу копятся только пики.
 * В памяти одновременно — один срез (несколько МБ) и массив пиков
 * (1000 отсчётов в секунду: 3 часа = 43 МБ, 10 часов = 144 МБ).
 *
 * ПОЧЕМУ ИМЕННО ТАК, А НЕ ИНАЧЕ
 *   · mp4box.js уже вендорён и уже читает наши файлы кусками (mp4box-probe.js
 *     кормит его `appendBuffer` со сдвигом) — демуксер писать не надо;
 *   · WebCodecs AudioDecoder принимает по одному кадру и отдаёт по одному
 *     AudioData — тот же приём, что в mp3-decoder-streaming.js, где записано:
 *     «пятичасовой подкаст помещается в ту же кучу, что тридцатисекундный
 *     клип». Там он сделан для mp3; здесь — для mp4/AAC, которого не хватало.
 *
 * ⚠️ 30.09.2026: сказанное выше про mp4box оказалось неправдой, когда память
 * померили ВСЕМ деревом процессов, а не кучей V8: поток держал каждый
 * прочитанный срез (у видео — весь файл) и копил кадры в очереди декодера.
 * Главный путь теперь — потоковый режим WebCodecsAudioDecode.decodeRange
 * (rangePeaks ниже, замеры там же); поток через mp4box остался запасным —
 * для фрагментированного mp4 и того, что decodeRange не открывает.
 *
 * API
 *   AudioPeaksStream.supported(file) → bool
 *   await AudioPeaksStream.peaks(file, { rate, onProgress, signal })
 *     → { samples: Float32Array, sampleRate, duration, channels }
 *   `samples` — огибающая: максимум модуля в каждом окне. Именно максимум, а
 *   не среднее: волна должна показывать всплески, а усреднение их съедает.
 */
(function (global) {
'use strict';

var VENDOR = '/vendor/mp4box/mp4box.all.min.js';
var SLICE = 4 * 1024 * 1024;      /* срез чтения с диска */
var DEFAULT_RATE = 1000;          /* замер: корреляция 0.9995 с полным декодом */

function isMp4(file) {
    var n = String((file && file.name) || '').toLowerCase();
    var t = String((file && file.type) || '').toLowerCase();
    return /\.(m4a|mp4|m4b|m4r|mov)$/.test(n) || /mp4|m4a|quicktime/.test(t);
}

/* `kind` — what the bytes are (FileMagic.identify), when the caller knows it;
   without it the name and MIME decide, as before. MPEG audio streams through
   Mp3DecoderStream (WebCodecs) — no FFmpeg engine to download first, which on a
   phone was most of the wait (/trim, October 2026: a third of the hour-long MP3s
   opened on phones never showed their waveform; people left after 50–120 s). */
function isMpeg(file, kind) {
    if (kind) return kind === 'mpeg';
    var n = String((file && file.name) || '').toLowerCase();
    return /\.(mp3|mpga)$/.test(n) || /audio\/(mpeg|mp3)$/.test(String((file && file.type) || '').toLowerCase());
}
function supported(file, kind) {
    if (!(global.AudioDecoder && global.EncodedAudioChunk)) return false;
    if (kind ? kind === 'mp4' : isMp4(file)) return true;
    return isMpeg(file, kind) && !!global.Mp3DecoderStream && !!global.Mp3FrameTrim;
}

/* MPEG: frames one by one through WebCodecs, only the envelope kept. Throws on
   a stream it cannot read (the caller's FFmpeg lane takes it). */
async function mpegPeaks(file, rate, signal, onProgress) {
    var D = global.Mp3DecoderStream;
    var buf = new Float32Array(Math.ceil(600 * rate)), len = 0, bucket = 0, bMax = 0, per = 0, ch = 2;
    await D.decode(file, {
        signal: signal,
        onProgress: function (p) { if (onProgress) { try { onProgress(p > 1 ? p / 100 : p); } catch (_) {} } },
        onChunk: function (s) {
            if (!per) { per = s.sampleRate / rate; ch = s.channelData.length; }
            var c0 = s.channelData[0], c1 = s.channelData[1] || null;
            for (var i = 0; i < c0.length; i++) {
                var v = c0[i] < 0 ? -c0[i] : c0[i];
                if (c1) { var w = c1[i] < 0 ? -c1[i] : c1[i]; if (w > v) v = w; }
                if (v > bMax) bMax = v;
                if (++bucket >= per) {
                    if (len >= buf.length) { var nb = new Float32Array(buf.length * 2); nb.set(buf); buf = nb; }
                    buf[len++] = bMax; bucket -= per; bMax = 0;
                }
            }
        },
    });
    if (bucket > 0) { if (len >= buf.length) { var nb2 = new Float32Array(len + 1); nb2.set(buf); buf = nb2; } buf[len++] = bMax; }
    if (!len) throw new Error('mpeg peaks: nothing decoded');
    return { samples: buf.subarray(0, len), sampleRate: rate, duration: len / rate, channels: ch, lane: 'mpeg' };
}

function loadMp4box() {
    if (global.MP4Box) return Promise.resolve(global.MP4Box);
    return new Promise(function (resolve, reject) {
        var s = document.createElement('script');
        s.src = VENDOR;
        s.onload = function () { global.MP4Box ? resolve(global.MP4Box) : reject(new Error('MP4Box не поднялся')); };
        s.onerror = function () { reject(new Error('не удалось загрузить mp4box')); };
        document.head.appendChild(s);
    });
}

/* AudioSpecificConfig из esds — WebCodecs без него не сконфигурирует AAC.
   Достаём из дерева боксов mp4box, а не парсим байты заново. */
function ascFromTrack(mp4file, trackId) {
    try {
        var t = mp4file.getTrackById(trackId);
        var entry = t.mdia.minf.stbl.stsd.entries[0];
        var esds = entry.esds || (entry.getCodecBox && entry.getCodecBox());
        if (esds && esds.esd) {
            var dec = esds.esd.descs && esds.esd.descs[0];
            var cfg = dec && dec.descs && dec.descs[0];
            if (cfg && cfg.data) return new Uint8Array(cfg.data);
        }
    } catch (_) {}
    return null;
}

/* Огибающая через потоковый режим WebCodecsAudioDecode.decodeRange (30.09.2026).
   Поток ниже (mp4box, срезы подряд) держал В ПАМЯТИ ВСЁ, что прочёл: mp4box
   отпускает срез, только когда использован каждый его байт, а байты картинки не
   используются никогда, и у файла с moov в хвосте кадры до конца неизвестны.
   Замер (рабочий набор дерева браузера, Edge, свежий профиль на файл): клип
   114 МБ +126 МБ, 572 МБ +677 (moov в хвосте) и +611 (faststart); m4a 212 МБ /
   3,6 ч — +1270 МБ: у декодера не было обратного давления, кадры копились в
   очереди. На видео 3,2 ГБ /add-music-to-video держал 4,6 ГБ. Прежние «112 МБ»
   мерили кучу V8, а буферы файла и кадры декодера живут вне неё.
   decodeRange читает ftyp+moov, берёт таблицу кадров и читает только байты
   звука окнами ≤ 8 МБ (или подряд окнами, если кадры разбросаны по одному —
   readThrough), очередь декодера ≤ 32; наружу — только пики. */
async function rangePeaks(file, rate, signal, onProgress) {
    var WAD = global.WebCodecsAudioDecode;
    var peaksBuf = null, peaksLen = 0, bucket = 0, bucketMax = 0, containerDur = 0, channels = 2, grow = false;
    var res = await WAD.decodeRange(file, null, {
        from: 0, sinkChannels: 1, signal: signal, onProgress: onProgress,
        onInfo: function (inf) {
            containerDur = inf.movieSec > 0 ? inf.movieSec : inf.trackSec;
            channels = inf.channels || 2;
            /* точный размер из заголовка; нет длительности — растим удвоением */
            if (containerDur > 0) peaksBuf = new Float32Array(Math.ceil(containerDur * rate) + 8);
            else { grow = true; peaksBuf = new Float32Array(Math.ceil(60 * rate)); }
        },
        sink: function (planes, sr) {
            var chan0 = planes[0], per = sr / rate;
            for (var i = 0; i < chan0.length; i++) {
                var v = chan0[i] < 0 ? -chan0[i] : chan0[i];
                if (v > bucketMax) bucketMax = v;
                if (++bucket >= per) {
                    if (grow && peaksLen >= peaksBuf.length) { var nb = new Float32Array(peaksBuf.length * 2); nb.set(peaksBuf); peaksBuf = nb; }
                    if (peaksLen < peaksBuf.length) peaksBuf[peaksLen++] = bucketMax;
                    bucket = 0; bucketMax = 0;
                }
            }
        }
    });
    if (bucket > 0 && peaksBuf && peaksLen < peaksBuf.length) peaksBuf[peaksLen++] = bucketMax;
    var dur = containerDur > 0 ? containerDur : peaksLen / rate;
    var need = Math.round(dur * rate);
    var samples = (peaksBuf && need <= peaksBuf.length)
        ? (peaksBuf.length === need ? peaksBuf : peaksBuf.subarray(0, need))
        : new Float32Array(need);
    return { samples: samples, sampleRate: rate, duration: dur, channels: res.channels || channels, lane: 'range' };
}

async function peaks(file, opts) {
    opts = opts || {};
    var rate = opts.rate || DEFAULT_RATE;
    var signal = opts.signal || null;
    var onProgress = typeof opts.onProgress === 'function' ? opts.onProgress : null;

    if (isMpeg(file, opts.kind) && !(opts.kind ? opts.kind === 'mp4' : isMp4(file))) return await mpegPeaks(file, rate, signal, onProgress);

    /* window.__peaksLane = 'mp4box' — только для стенда (A/B двух путей). */
    var WAD = global.WebCodecsAudioDecode;
    if (WAD && typeof WAD.decodeRange === 'function' && global.__peaksLane !== 'mp4box') {
        try {
            return await rangePeaks(file, rate, signal, onProgress);
        } catch (e) {
            if (signal && signal.aborted) throw new Error('отменено');
            /* Нет звуковой дорожки — свойство файла, а не повод читать его
               весь вторым путём (у видео с moov в хвосте — весь файл в память). */
            if (/no audio track/.test(String(e && e.message))) {
                var noAudio = new Error('в файле нет звуковой дорожки');
                noAudio.expected = true;
                throw noAudio;
            }
            /* фрагментированный mp4, moov больше потолка, звук не AAC — это
               читает поток ниже */
        }
    }

    var MP4Box = await loadMp4box();
    var mp4 = MP4Box.createFile();

    var track = null, decoder = null, containerDur = 0;
    /* ⚠️ НЕ обычный массив. ЗАМЕРЕНО 2026-08-11 на файле 3 ч 05 мин: копить
       11 млн чисел в JS-массиве и лишь потом превращать в типизированный —
       это пик кучи 226 МБ при результате 42 МБ, впятеро больше нужного.
       На телефоне с 4 ГБ, где уже лежат 198 МБ файла, такой пик добивает
       вкладку. Длительность известна из контейнера ДО декодирования, поэтому
       выделяем точный массив сразу и пишем по индексу — без промежуточной
       копии и без перевыделений при росте. */
    var peaksBuf = null, peaksLen = 0, bucket = 0, bucketMax = 0, srcRate = 0;
    var pending = 0, finished = false, failed = null;

    /* Огибающая: копим МАКСИМУМ модуля в окне длиной (srcRate / rate). */
    function pushSamples(chan0) {
        var per = srcRate / rate;
        for (var i = 0; i < chan0.length; i++) {
            var v = chan0[i] < 0 ? -chan0[i] : chan0[i];
            if (v > bucketMax) bucketMax = v;
            if (++bucket >= per) {
                if (peaksBuf && peaksLen < peaksBuf.length) peaksBuf[peaksLen++] = bucketMax;
                bucket = 0; bucketMax = 0;
            }
        }
    }

    var ready = new Promise(function (resolve, reject) {
        mp4.onError = function (e) {
            /* Демуксер не разобрал контейнер — ожидаемый исход для битого или
               не-mp4 файла, вызывающий код мягко уходит на FFmpeg-лейн.
               Контракт err.expected=true (см. sentry-init.js): продуктовое
               поведение, не баг — Sentry такое не видит. */
            failed = new Error('mp4box: ' + e);
            failed.expected = true;
            reject(failed);
        };
        mp4.onReady = function (info) {
            track = (info.audioTracks && info.audioTracks[0]) || null;
            if (!track) {
                /* НАМЕРЕННЫЙ отказ: видео без звуковой дорожки — свойство
                   файла пользователя, не баг. Тот же контракт expected=true
                   (TIMBRICA-FRONTEND-EN: долетало до Sentry с /trim). */
                failed = new Error('в файле нет звуковой дорожки');
                failed.expected = true;
                return reject(failed);
            }
            srcRate = track.audio.sample_rate;
            /* Длительность берём из КОНТЕЙНЕРА, а не из числа пиков: демуксер
               не отдаёт последний неполный набор кадров, и по пикам выходило
               10860 с вместо 11082 (замер) — человек не смог бы выбрать
               концовку записи. Огибающая на этом хвосте будет ровной, но шкала
               и диапазон выбора остаются верными. */
            containerDur = (info.duration && info.timescale)
                ? info.duration / info.timescale
                : (track.duration && track.timescale ? track.duration / track.timescale : 0);
            /* Точный размер известен здесь: длительность из контейнера × частота.
               Запас в 8 отсчётов — на округление длительности. */
            if (containerDur > 0) {
                peaksBuf = new Float32Array(Math.ceil(containerDur * rate) + 8);
            }
            var desc = ascFromTrack(mp4, track.id);
            try {
                decoder = new global.AudioDecoder({
                    output: function (data) {
                        try {
                            var n = data.numberOfFrames;
                            var buf = new Float32Array(n);
                            /* Канал 0: огибающей второго канала не нужно, а
                               копировать оба значило бы удвоить расход. */
                            data.copyTo(buf, { planeIndex: 0, format: 'f32-planar' });
                            pushSamples(buf);
                        } catch (_) {} finally { data.close(); pending--; }
                    },
                    error: function (e) { failed = e; },
                });
                decoder.configure({
                    codec: track.codec,
                    sampleRate: srcRate,
                    numberOfChannels: track.audio.channel_count,
                    description: desc || undefined,
                });
            } catch (e) { failed = e; return reject(e); }
            mp4.setExtractionOptions(track.id, null, { nbSamples: 300 });
            mp4.start();
            resolve();
        };
        mp4.onSamples = function (_id, _user, samples) {
            for (var i = 0; i < samples.length; i++) {
                if (failed) break;
                var s = samples[i];
                try {
                    decoder.decode(new global.EncodedAudioChunk({
                        type: s.is_sync ? 'key' : 'delta',
                        timestamp: (s.cts / s.timescale) * 1e6,
                        duration: (s.duration / s.timescale) * 1e6,
                        data: s.data,
                    }));
                    pending++;
                } catch (e) { failed = e; break; }
                s.data = null;   /* отпускаем байты кадра сразу */
            }
            mp4.releaseUsedSamples(_id, samples[samples.length - 1].number);
        };
    });

    /* ⚠️ Отказ `ready` ВСЕГДА продублирован через `failed`: цикл кормления
       ниже сам бросает его (`if (failed) throw failed`) и выходит, НЕ дойдя
       до `await ready`. Отвергнутый и никем не ожидаемый промис оставался
       висеть → unhandledrejection долетал до Sentry с /trim, хотя вызывающий
       код свою копию ошибки поймал и мягко ушёл на FFmpeg-лейн
       (TIMBRICA-FRONTEND-EN). Глушим ИМЕННО дубликат: сама ошибка по-прежнему
       уходит наружу броском из peaks(). */
    ready.catch(function () {});

    /* Кормим демуксер срезами прямо с диска — файл целиком в память не читаем. */
    var offset = 0;
    while (offset < file.size) {
        if (signal && signal.aborted) throw new Error('отменено');
        if (failed) throw failed;
        var end = Math.min(offset + SLICE, file.size);
        var ab = await file.slice(offset, end).arrayBuffer();
        ab.fileStart = offset;
        mp4.appendBuffer(ab);
        /* ⚠️ Возвращаемое значение appendBuffer — ПОДСКАЗКА, куда демуксер
           хотел бы прыгнуть, а не «сколько съедено». Доверившись ей, первый же
           вызов уносил смещение в конец файла, цикл завершался после одного
           среза, и огибающая выходила длиной 259 с вместо 11082 — то есть
           2.4 % файла, ровно один срез. Кормим строго последовательно. */
        offset = end;
        if (onProgress) onProgress(offset / file.size);
        /* Даём циклу событий вздохнуть: без этого длинный файл держит поток и
           страница выглядит зависшей — та самая жалоба «крутит колёсико». */
        await new Promise(function (r) { setTimeout(r, 0); });
    }
    mp4.flush();
    await ready;
    if (failed) throw failed;

    /* ⚠️ Хвост. `onSamples` для последних кадров прилетает УЖЕ ПОСЛЕ flush(),
       асинхронно. Закрыв декодер сразу, мы теряли последние 222 с из 11082
       (замер) — человек не смог бы выбрать концовку записи. Ждём, пока
       демуксер перестанет подкидывать кадры, а декодер — их разбирать. */
    var idle = 0, lastSeen = -1;
    while (idle < 6) {
        await new Promise(function (r) { setTimeout(r, 60); });
        if (signal && signal.aborted) throw new Error('отменено');
        var seen = peaksLen + pending;
        if (seen === lastSeen) idle++; else { idle = 0; lastSeen = seen; }
    }

    /* Дожидаемся, пока декодер отдаст всё, что принял. */
    if (decoder) { try { await decoder.flush(); } catch (_) {} try { decoder.close(); } catch (_) {} }
    if (bucket > 0 && peaksBuf && peaksLen < peaksBuf.length) peaksBuf[peaksLen++] = bucketMax;

    var dur = containerDur > 0 ? containerDur : peaksLen / rate;
    var need = Math.round(dur * rate);
    /* Хвост уже нулевой — Float32Array создаётся обнулённым, а демуксер не
       отдаёт последний неполный набор кадров (замер: 10860 из 11082 с).
       Достраивать ничего не надо, достаточно взять нужную длину. */
    var samples = peaksBuf
        ? (peaksBuf.length === need ? peaksBuf : peaksBuf.subarray(0, need))
        : new Float32Array(need);
    return {
        samples: samples,
        sampleRate: rate,
        duration: dur,
        channels: (track && track.audio && track.audio.channel_count) || 2,
    };
}

global.AudioPeaksStream = { supported: supported, peaks: peaks, DEFAULT_RATE: DEFAULT_RATE };

})(window);
